import { createBrowserRouter, Navigate, useRouteError } from "react-router-dom";
import AppLayout from "./components/layout/AppLayout";
import AlumniLayout from "./components/layout/AlumniLayout";
import Login from "./pages/auth/Login";
import Signup from "./pages/auth/Signup";
import SetupWizard from "./pages/auth/SetupWizard";
import AlumniLogin from "./pages/auth/AlumniLogin";
import AlumniRegister from "./pages/auth/AlumniRegister";
import AlumniSetPassword from "./pages/auth/AlumniSetPassword";
import Dashboard from "./pages/dashboard/Dashboard";
import OffboardingList from "./pages/offboardings/OffboardingList";
import OffboardingDetail from "./pages/offboardings/OffboardingDetail";
import NewOffboarding from "./pages/offboardings/NewOffboarding";
import TemplateList from "./pages/templates/TemplateList";
import TemplateDetail from "./pages/templates/TemplateDetail";
import InterviewList from "./pages/interviews/InterviewList";
import KnowledgeBase from "./pages/knowledge/KnowledgeBase";
import KnowledgeGaps from "./pages/knowledge/KnowledgeGaps";
import Analytics from "./pages/analytics/Analytics";
import Assets from "./pages/assets/Assets";
import Trends from "./pages/analytics/Trends";
import Alumni from "./pages/alumni/Alumni";
import AlumniProfile from "./pages/alumni/AlumniProfile";
import AlumniJobs from "./pages/alumni-portal/AlumniJobs";
import AlumniUpdates from "./pages/alumni-portal/AlumniUpdates";
import AlumniThreads from "./pages/alumni-portal/AlumniThreads";
import AlumniGigs from "./pages/alumni-portal/AlumniGigs";
import AlumniDocRequests from "./pages/alumni-portal/AlumniDocRequests";
import AlumniSurveys from "./pages/alumni-portal/AlumniSurveys";
import Settings from "./pages/settings/Settings";
import TeamSettings from "./pages/settings/TeamSettings";
import BillingSettings from "./pages/settings/BillingSettings";
import Help from "./pages/help/Help";
import PortalEntry from "./pages/portal/PortalEntry";
import SurveyPage from "./pages/survey/SurveyPage";

function NotFound() {
  const error = useRouteError() as { status?: number } | undefined;
  const is404 = !error || error.status === 404;
  return (
    <div className="min-h-screen flex items-center justify-center bg-warm/30 p-4">
      <div className="text-center">
        <p className="text-6xl font-display text-teal mb-4">
          {is404 ? "404" : "Error"}
        </p>
        <h1 className="text-xl font-semibold text-navy mb-2">
          {is404 ? "Page not found" : "Something went wrong"}
        </h1>
        <p className="text-sm text-mist mb-6">
          {is404
            ? "The page you're looking for doesn't exist."
            : "An unexpected error occurred."}
        </p>
        <a
          href="/dashboard"
          className="text-sm bg-teal text-white px-4 py-2 rounded-lg hover:bg-teal/90 transition-colors"
        >
          Go to Dashboard
        </a>
      </div>
    </div>
  );
}

export const router = createBrowserRouter([
  // Public routes
  { path: "/login", element: <Login /> },
  { path: "/signup", element: <Signup /> },
  { path: "/portal/:token", element: <PortalEntry /> },
  { path: "/alumni-login", element: <AlumniLogin /> },
  { path: "/alumni-register", element: <AlumniRegister /> },
  { path: "/alumni-setup", element: <AlumniSetPassword /> },
  { path: "/survey/:token", element: <SurveyPage /> },

  // Setup route
  { path: "/setup", element: <SetupWizard /> },
  { path: "/portal", element: <PortalEntry /> },

  // Alumni portal routes
  {
    path: "/alumni-portal",
    element: <AlumniLayout />,
    children: [
      { index: true, element: <Navigate to="/alumni-portal/profile" replace /> },
      { path: "profile", element: <AlumniProfile /> },
      { path: "jobs", element: <AlumniJobs /> },
      { path: "updates", element: <AlumniUpdates /> },
      { path: "threads", element: <AlumniThreads /> },
      { path: "gigs", element: <AlumniGigs /> },
      { path: "documents", element: <AlumniDocRequests /> },
      { path: "surveys", element: <AlumniSurveys /> },
    ],
  },
  {
    path: "/",
    element: <AppLayout />,
    errorElement: <NotFound />,
    children: [
      { index: true, element: <Navigate to="/dashboard" replace /> },
      { path: "dashboard", element: <Dashboard /> },
      { path: "offboardings", element: <OffboardingList /> },
      { path: "offboardings/new", element: <NewOffboarding /> },
      { path: "offboardings/:id", element: <OffboardingDetail /> },
      { path: "assets", element: <Assets /> },
      { path: "templates", element: <TemplateList /> },
      { path: "templates/:id", element: <TemplateDetail /> },
      { path: "interviews", element: <InterviewList /> },
      { path: "knowledge", element: <KnowledgeBase /> },
      { path: "knowledge/gaps", element: <KnowledgeGaps /> },
      { path: "analytics", element: <Analytics /> },
      { path: "analytics/trends", element: <Trends /> },
      { path: "alumni", element: <Alumni /> },
      { path: "settings", element: <Settings /> },
      { path: "settings/team", element: <TeamSettings /> },
      { path: "settings/billing", element: <BillingSettings /> },
      { path: "help", element: <Help /> },
    ],
  },
  { path: "*", element: <NotFound /> },
]);
